import React from 'react'
import { chatPermissions } from '@ztechapps/chat.common'
import { useConfirmDialog, useError, useRole } from '@ztechapps/common.hook'
import { BusyButtonLink } from '@ztechapps/webui.button'
import { ConversationActionItem } from './ConversationActionItem'
import { useConversationContext } from './Context'
import { useUnlinkChatFromDocument } from './useUnlinkChatFromDocument'

export type ClearConversationProps = {
  /** The document-side conversation id to detach. */
  conversationId: string
  /** Renders a small icon control beside a document cell instead of a dropdown menu item. */
  compact?: boolean
}

// A menu item of the conversation actions dropdown that detaches the attached conversation from its document,
// after a confirm. Without the edit permission the item stays disabled with the reason, as AttachConversation.tsx:23-34
// does (both render ConversationActionItem).
export const ClearConversation = ({ conversationId, compact }: ClearConversationProps) => {
  const { hasRole: canEdit } = useRole({ roleName: chatPermissions.canEdit.value })
  const { onConversationLinked } = useConversationContext()
  const [unlinkChatFromDocument] = useUnlinkChatFromDocument()
  const { onError } = useError()

  // The host re-reads the document so its `activeConversationId` empties and the conversation card follows.
  // A refused unlink rejects here and is toasted, since no form is open to show it.
  const { onConfirm } = useConfirmDialog({
    title: 'Clear the attached conversation?',
    text: 'The conversation is unlinked from this document and stays in the inbox. Participants already added to the document, the requester and the cc emails are kept. Sending a message from this record will start a new conversation.',
    buttons: { confirm: { text: 'Clear conversation' } },
    confirmed: async () => {
      try {
        await unlinkChatFromDocument({ chatId: conversationId })
        await onConversationLinked?.()
      } catch (e) {
        onError(e)
      }
    },
  })

  const disabledReason = canEdit ? undefined : 'You need the Conversation edit permission to clear conversations.'

  if (compact) {
    return (
      <BusyButtonLink
        icon="unlink"
        title="Clear conversation"
        className="ms-2"
        disabledReason={disabledReason}
        onClick={() => onConfirm()}
      />
    )
  }

  return (
    <ConversationActionItem
      icon="unlink"
      label="Clear conversation"
      onClick={() => onConfirm()}
      disabledReason={disabledReason}
    />
  )
}
